import { T, Bone } from '../../reportTokens';
import { getCurrencySymbol } from '../../../../utils/currency';
import type { OrderDetailResponse } from '../../../../interface/clickHouseInterface';
import { usePullToRefresh } from '../../../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../../../utils/pullToRefresh/PullToRefreshIndicator';
import './style.scss';

// ─── helpers ─────────────────────────────────────────────────────────────────

const num = (v: any): number => Number(v ?? 0) || 0;

const fmtCurrency = (v: any) =>
    `${getCurrencySymbol()}${num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtPhone = (raw: any): string => {
    if (!raw) return '—';
    const digits = String(raw).replace(/\D/g, '');
    if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    return String(raw);
};

const fmtOrderTime = (raw: any): string => {
    if (!raw) return '—';
    try {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return String(raw);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
        return String(raw);
    }
};

const statusColor = (s: string): string => {
    if (/refund|cancel/i.test(s)) return T.red;
    if (/partial/i.test(s)) return T.orange;
    return T.green;
};

// ─── types ───────────────────────────────────────────────────────────────────

interface NormalisedItem {
    name: string;
    modifiers: string | null;
    qty: number;
    unitPrice: number;
    lineTotal: number;
}

interface NormalisedDetail {
    orderTime: string;
    orderStatus: string;
    customerName: string | null;
    phone: string;
    email: string | null;
    guests: number | null;
    tableName: string | null | undefined;
    deliveryAddress: string | null;
    isOffPremise: boolean;
    staffName: string | null;
    items: NormalisedItem[];
    removedItems: OrderDetailResponse['removedItems'];
    subtotal: number;
    discount: number;
    tax: number;
    tip: number;
    gratuityAmt: number;
    deliveryCharges: number;
    convenienceFee: number;
    cardProcessingFee: number;
    total: number;
}

// ─── normalise typed OR legacy payload into one shape ────────────────────────

function normalise(detail: OrderDetailResponse | null, legacyDetail: any): NormalisedDetail | null {
    const isTyped = !!detail;

    if (!isTyped && !legacyDetail) return null;

    const orderType: string = isTyped
        ? detail!.orderType
        : String(legacyDetail?.orderType ?? legacyDetail?.channelName ?? legacyDetail?.channel ?? '');

    const items: NormalisedItem[] = isTyped
        ? detail!.items.map(it => ({
            name: it.itemName,
            modifiers: it.modifiers || null,
            qty: it.qty,
            unitPrice: it.unitPrice,
            lineTotal: it.lineTotal,
        }))
        : (legacyDetail?.items ?? legacyDetail?.orderItems ?? legacyDetail?.lineItems ?? []).map((it: any) => ({
            name: it.itemName ?? it.name ?? it.menuItemName ?? '—',
            modifiers: it.modifiers ?? it.modifications ?? it.addOns ?? it.specialInstructions ?? null,
            qty: Number(it.quantity ?? it.qty ?? it.count ?? 1),
            unitPrice: Number(it.unitPrice ?? it.price ?? it.itemPrice ?? 0),
            // FE calc: unitPrice × qty as last fallback
            lineTotal: Number(it.lineTotal ?? it.totalPrice ?? it.amount ?? (it.unitPrice ?? it.price ?? 0) * (it.quantity ?? it.qty ?? 1)),
        }));

    const discount = isTyped
        ? detail!.discounts
        : Number(legacyDetail?.discount ?? legacyDetail?.discountAmount ?? legacyDetail?.totalDiscount ?? 0);

    const tax = isTyped ? detail!.tax : Number(legacyDetail?.tax ?? legacyDetail?.taxAmount ?? legacyDetail?.totalTax ?? 0);
    const tip = isTyped ? detail!.tip : Number(legacyDetail?.tip ?? legacyDetail?.tipAmount ?? 0);

    // FE calc: sum of lineTotals as subtotal fallback for legacy
    const subtotal = isTyped
        ? detail!.subtotal
        : (items.reduce((s, it) => s + it.lineTotal, 0) || Number(legacyDetail?.subTotal ?? legacyDetail?.subtotal ?? 0));

    // FE calc: subtotal - discount + tax + tip as total fallback for legacy
    const total = isTyped
        ? detail!.total
        : Number(legacyDetail?.amount ?? legacyDetail?.totalAmount ?? legacyDetail?.total ?? legacyDetail?.orderTotal ?? (subtotal - discount + tax + tip));

    return {
        orderTime: fmtOrderTime(isTyped ? detail!.orderTime : (legacyDetail?.orderTime ?? legacyDetail?.completedAt ?? legacyDetail?.createdAt ?? legacyDetail?.time)),
        orderStatus: isTyped ? detail!.orderStatus : (legacyDetail?.orderStatus ?? legacyDetail?.status ?? 'Completed'),
        customerName: isTyped ? (detail!.customerName || null) : (legacyDetail?.customerName ?? legacyDetail?.customer ?? legacyDetail?.guestName ?? null),
        phone: fmtPhone(isTyped ? detail!.customerPhone : (legacyDetail?.customerPhone ?? legacyDetail?.phone ?? legacyDetail?.mobileNumber ?? null)),
        email: isTyped ? (detail!.customerEmail || null) : (legacyDetail?.customerEmail ?? legacyDetail?.email ?? null),
        guests: isTyped ? (detail!.guests ?? null) : (legacyDetail?.guests ?? null),
        tableName: isTyped
            ? (detail!.tableName !== undefined ? (detail!.tableName || null) : undefined)
            : ('tableName' in (legacyDetail ?? {}) ? (legacyDetail.tableName || null) : undefined),
        deliveryAddress: isTyped ? detail!.deliveryAddress : (legacyDetail?.deliveryAddress ?? legacyDetail?.address ?? null),
        isOffPremise: /delivery|doordash|uber|pickup|catering/i.test(orderType),
        staffName: isTyped ? (detail!.staffName || null) : (legacyDetail?.staffName ?? legacyDetail?.serverName ?? legacyDetail?.employeeName ?? legacyDetail?.staff ?? null),
        items,
        removedItems: isTyped ? (detail!.removedItems ?? []) : [],
        subtotal,
        discount,
        tax,
        tip,
        gratuityAmt: isTyped ? (detail!.gratuity ?? 0) : Number(legacyDetail?.gratuity ?? 0),
        deliveryCharges: isTyped ? (detail!.deliveryCharges ?? 0) : Number(legacyDetail?.deliveryCharges ?? 0),
        convenienceFee: isTyped ? (detail!.convenienceFee ?? 0) : Number(legacyDetail?.convenienceFee ?? 0),
        cardProcessingFee: isTyped ? (detail!.cardProcessingFee ?? 0) : Number(legacyDetail?.cardProcessingFee ?? 0),
        total,
    };
}

// ─── sub-components ──────────────────────────────────────────────────────────

const ITEM_GRID = '2fr 1.2fr 0.5fr 0.7fr 0.8fr';

function ItemsTable({ items }: { items: NormalisedItem[] }) {
    return (
        <div className="ods-card">
            <div className="ods-card__label">Items</div>
            <div className="ods-items-header" style={{ gridTemplateColumns: ITEM_GRID }}>
                {['Item', 'Modifiers', 'Qty', 'Unit', 'Total'].map(h => (
                    <div key={h} className="ods-col-head">{h}</div>
                ))}
            </div>
            {items.map((it, idx) => (
                <div
                    key={idx}
                    className="ods-item-row"
                    style={{
                        gridTemplateColumns: ITEM_GRID,
                        borderBottom: idx < items.length - 1 ? `1px solid ${T.divider}` : 'none',
                    }}
                >
                    <div className="ods-item-name">{it.name}</div>
                    <div className="ods-item-mod">
                        {it.modifiers
                            ? Array.isArray(it.modifiers) ? (it.modifiers as string[]).join(', ') : String(it.modifiers)
                            : '—'}
                    </div>
                    <div className="ods-item-qty">{it.qty}</div>
                    <div className="ods-item-unit">{fmtCurrency(it.unitPrice)}</div>
                    <div className="ods-item-total">{fmtCurrency(it.lineTotal)}</div>
                </div>
            ))}
        </div>
    );
}

function CancelledItemsTable({ items }: { items: NonNullable<OrderDetailResponse['removedItems']> }) {
    return (
        <div className="ods-card ods-card--cancelled" style={{ border: `1px solid ${T.red}50`, background: `${T.red}08` }}>
            <div className="ods-card__label" style={{ color: T.red }}>Cancelled Items</div>
            <div className="ods-items-header" style={{ gridTemplateColumns: ITEM_GRID }}>
                {['Item', 'Modifiers', 'Qty', 'Unit', 'Total'].map(h => (
                    <div key={h} className="ods-col-head">{h}</div>
                ))}
            </div>
            {items.map((ri, idx) => (
                <div
                    key={idx}
                    className="ods-item-row"
                    style={{
                        gridTemplateColumns: ITEM_GRID,
                        borderBottom: idx < items.length - 1 ? `1px solid ${T.divider}` : 'none',
                    }}
                >
                    <div>
                        <div className="ods-item-name">{ri.item}</div>
                        {ri.cancelReason && (
                            <div className="ods-cancel-reason" style={{ color: T.orange }}>
                                <span style={{ color: T.t3 }}>Reason: </span>{ri.cancelReason}
                            </div>
                        )}
                    </div>
                    <div className="ods-item-mod">
                        {ri.modifiers && ri.modifiers.length > 0 ? ri.modifiers.map(m => m.name).join(', ') : '—'}
                    </div>
                    <div className="ods-item-qty">{ri.quantity}</div>
                    <div className="ods-item-unit">{ri.unitPrice != null ? fmtCurrency(ri.unitPrice) : '—'}</div>
                    <div className="ods-item-total" style={{ color: T.red }}>{fmtCurrency(ri.total)}</div>
                </div>
            ))}
        </div>
    );
}

function SummaryCard({ d }: { d: NormalisedDetail }) {
    const rows = [
        { label: 'Subtotal', val: fmtCurrency(d.subtotal), color: T.t1 },
        ...(d.discount > 0 ? [{ label: 'Discounts', val: `–${fmtCurrency(d.discount)}`, color: T.orange }] : []),
        { label: 'Tax', val: fmtCurrency(d.tax), color: T.t1 },
        { label: 'Tip', val: fmtCurrency(d.tip), color: T.green },
        ...(d.gratuityAmt > 0 ? [{ label: 'Gratuity', val: fmtCurrency(d.gratuityAmt), color: T.green }] : []),
        ...(d.deliveryCharges > 0 ? [{ label: 'Delivery Charges', val: fmtCurrency(d.deliveryCharges), color: T.t1 }] : []),
        ...(d.convenienceFee > 0 ? [{ label: 'Convenience Fee', val: fmtCurrency(d.convenienceFee), color: T.t1 }] : []),
        ...(d.cardProcessingFee > 0 ? [{ label: 'Card Processing Fee', val: fmtCurrency(d.cardProcessingFee), color: T.t1 }] : []),
    ];

    return (
        <div className="ods-card">
            <div className="ods-card__label">Summary</div>
            {rows.map(row => (
                <div key={row.label} className="ods-summary-row" style={{ borderBottom: `1px solid ${T.divider}` }}>
                    <span className="ods-summary-label">{row.label}</span>
                    <span className="ods-summary-val" style={{ color: row.color }}>{row.val}</span>
                </div>
            ))}
            <div className="ods-summary-total">
                <span className="ods-total-label">Total</span>
                <span className="ods-total-val" style={{ color: T.accent }}>{fmtCurrency(d.total)}</span>
            </div>
        </div>
    );
}

// ─── main component ──────────────────────────────────────────────────────────

export interface OrderDetailSliderProps {
    visible: boolean;
    onClose: () => void;
    /** Shown in the panel header */
    orderNo?: string | number | null;
    /** Shown as subtitle in the panel header */
    orderType?: string | null;
    loading: boolean;
    error: boolean;
    onRetry: () => void;
    /** Typed Redux response (Today / ClickHouse path) */
    detail: OrderDetailResponse | null;
    /** Raw API response used as fallback for non-Today path in SalesScreen */
    legacyDetail?: any;
}

export function OrderDetailSlider({
    visible,
    onClose,
    orderNo,
    orderType,
    loading,
    error,
    onRetry,
    detail,
    legacyDetail,
}: OrderDetailSliderProps) {
    const { scrollRef, pullDistance, isRefreshing } = usePullToRefresh({
        onRefresh: onRetry,
        disabled: !visible,
    });

    if (!visible) return null;

    const d = normalise(detail, legacyDetail);

    return (
        <div className="ods-root" onClick={onClose}>
            <div className="ods-panel" onClick={e => e.stopPropagation()} style={{ background: T.surface }}>
                <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />

                {/* Header */}
                <div className="ods-header" style={{ borderBottom: `1px solid ${T.divider}` }}>
                    <div>
                        <div className="ods-header__title" style={{ color: T.t1 }}>
                            Order #{orderNo ?? '—'}
                        </div>
                        <div className="ods-header__subtitle" style={{ color: T.t3 }}>
                            {orderType ?? '—'}
                        </div>
                    </div>
                    <button
                        className="ods-close-btn"
                        onClick={onClose}
                        style={{ border: `1px solid ${T.cardBorder}`, background: T.hover, color: T.t2 }}
                    >
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div className="ods-body" ref={scrollRef}>
                    {loading ? (
                        <div className="ods-loading">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Bone key={i} w={null} h={14} style={{ marginBottom: 12 }} />
                            ))}
                        </div>
                    ) : error ? (
                        <div className="ods-error">
                            <span className="material-symbols-rounded ods-error__icon" style={{ color: T.t3 }}>
                                error_outline
                            </span>
                            <div className="ods-error__msg" style={{ color: T.t3 }}>
                                Failed to load order details.
                            </div>
                            <button
                                className="ods-error__retry"
                                onClick={onRetry}
                                style={{ color: T.accent }}
                            >
                                Retry
                            </button>
                        </div>
                    ) : !d ? (
                        <div className="ods-empty" style={{ color: T.t3 }}>
                            No order details available.
                        </div>
                    ) : (
                        <>
                            {/* Time + Status */}
                            <div className="ods-meta">
                                <div className="ods-meta__block">
                                    <div className="ods-field-label" style={{ color: T.t3 }}>Order Time</div>
                                    <div className="ods-field-value" style={{ color: T.t1 }}>{d.orderTime}</div>
                                </div>
                                <div>
                                    <div className="ods-field-label" style={{ color: T.t3 }}>Status</div>
                                    <span
                                        className="ods-status-badge"
                                        style={{ color: statusColor(d.orderStatus), background: `${statusColor(d.orderStatus)}15` }}
                                    >
                                        {d.orderStatus}
                                    </span>
                                </div>
                            </div>

                            {/* Customer */}
                            <div className="ods-card">
                                <div className="ods-card__label" style={{ color: T.t3 }}>Customer</div>
                                <div className="ods-customer-grid">
                                    <div>
                                        <div className="ods-field-label" style={{ color: T.t3 }}>Name</div>
                                        <div className="ods-customer-name" style={{ color: T.t1 }}>{d.customerName || 'Walk-in'}</div>
                                    </div>
                                    <div>
                                        <div className="ods-field-label" style={{ color: T.t3 }}>Phone</div>
                                        <div className="ods-field-value" style={{ color: T.t1 }}>{d.phone}</div>
                                    </div>
                                    {d.email && (
                                        <div>
                                            <div className="ods-field-label" style={{ color: T.t3 }}>Email</div>
                                            <div className="ods-field-value" style={{ color: T.t1 }}>{d.email}</div>
                                        </div>
                                    )}
                                    {d.guests != null && (
                                        <div>
                                            <div className="ods-field-label" style={{ color: T.t3 }}>Guests</div>
                                            <div className="ods-field-value" style={{ color: T.t1 }}>{d.guests}</div>
                                        </div>
                                    )}
                                    {d.tableName !== undefined && (
                                        <div>
                                            <div className="ods-field-label" style={{ color: T.t3 }}>Table No.</div>
                                            <div className="ods-field-value" style={{ color: T.t1 }}>{d.tableName || '—'}</div>
                                        </div>
                                    )}
                                    {d.isOffPremise && d.deliveryAddress && (
                                        <div className="ods-customer-address">
                                            <div className="ods-field-label" style={{ color: T.t3 }}>Delivery Address</div>
                                            <div className="ods-field-value" style={{ color: T.t1 }}>{d.deliveryAddress}</div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Items */}
                            {d.items.length > 0 && <ItemsTable items={d.items} />}

                            {/* Cancelled Items */}
                            {d.removedItems && d.removedItems.length > 0 && (
                                <CancelledItemsTable items={d.removedItems} />
                            )}

                            {/* Summary */}
                            <SummaryCard d={d} />

                            {/* Staff */}
                            {d.staffName && (
                                <div className="ods-card">
                                    <div className="ods-card__label" style={{ color: T.t3 }}>Staff</div>
                                    <div className="ods-summary-row">
                                        <span className="ods-summary-label">Server</span>
                                        <span className="ods-staff-name" style={{ color: T.accent }}>{d.staffName}</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

            </div>
        </div>
    );
}
